import { motion } from "framer-motion";
import { Dumbbell, Target, Users, Trophy } from "lucide-react";
import { isPresent, hasItems } from "./helpers";

const whyIconMap = {
  dumbbell: Dumbbell,
  target: Target,
  users: Users,
  trophy: Trophy,
};

export default function WhyDVX({ section }) {
  if (!section) return null;

  const titleLines = isPresent(section.title) ? section.title.split("\n") : [];
  const showFeatures = hasItems(section.features);

  if (
    !isPresent(section.badge) &&
    !titleLines.length &&
    !isPresent(section.description) &&
    !showFeatures &&
    !isPresent(section.image)
  )
    return null;

  return (
    <section
      data-testid="why-dvx"
      id="about"
      className="py-24 md:py-32 bg-[var(--dvx-surface)] transition-colors duration-300 scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {isPresent(section.badge) && (
              <h3 className="text-[var(--dvx-primary)] font-oswald text-xl font-semibold uppercase tracking-[0.20.em]">
                {section.badge}
              </h3>
            )}
            {titleLines.length > 0 && (
              <h2 className="mt-4 font-oswald text-4xl md:text-5xl font-bold text-[var(--dvx-text)] uppercase tracking-tight">
                {titleLines.map((line, i) => (
                  <span key={i}>
                    {line}
                    {i < titleLines.length - 1 && <br />}
                  </span>
                ))}
              </h2>
            )}
            {isPresent(section.description) && (
              <p className="mt-6 text-[var(--dvx-text-muted)] font-manrope text-lg leading-relaxed">
                {section.description}
              </p>
            )}
            {showFeatures && (
              <div className="mt-10 grid sm:grid-cols-2 gap-6">
                {section.features.map((feature, i) => {
                  if (!isPresent(feature.title)) return null;
                  const Icon = whyIconMap[feature.icon] || Target;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className="flex gap-4"
                    >
                      <div className="w-12 h-12 flex-shrink-0 bg-[var(--dvx-primary)]/10 border border-[var(--dvx-primary)]/30 flex items-center justify-center">
                        <Icon className="text-[var(--dvx-primary)]" size={24} />
                      </div>
                      <div>
                        <h4 className="font-oswald text-[var(--dvx-text)] font-bold uppercase tracking-wide">
                          {feature.title}
                        </h4>
                        {isPresent(feature.description) && (
                          <p className="text-[var(--dvx-text-muted)] text-sm mt-1 font-manrope">
                            {feature.description}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>

          {isPresent(section.image) && (
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative">
                <img
                  src={section.image}
                  alt="DVX Training"
                  className="w-full aspect-[4/5] object-cover"
                />
                {/* <div className="absolute -bottom-4 -right-4 w-32 h-32 border-2 border-[var(--dvx-primary)]" /> */}
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-[var(--dvx-primary)]/20" />
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
